import { useState, useEffect } from "react";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import RecurrenceBuilder, { DEFAULT_RECURRENCE } from "@/components/RecurrenceBuilder";
import ChannelPicker from "@/components/ChannelPicker";
import VoiceScriptField from "@/components/VoiceScriptField";
import api, { formatApiError } from "@/lib/api";

const empty = () => ({
  title: "",
  notes: "",
  time: "09:00",
  start_date: new Date().toISOString().slice(0, 10),
  recurrence: { ...DEFAULT_RECURRENCE },
  channels: ["sms"],
  voice_script: "",
});

export default function ServiceLoopDialog({ open, onClose, onSaved, client, loop }) {
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(loop ? { ...empty(), ...loop, notes: loop.notes ?? "", voice_script: loop.voice_script ?? "" } : empty());
  }, [open, loop]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Service title is required");
    setBusy(true);
    try {
      const payload = {
        title: form.title, notes: form.notes, time: form.time, start_date: form.start_date,
        recurrence: form.recurrence, channels: form.channels, voice_script: form.voice_script,
        is_checkin: !!form.is_checkin,
      };
      if (loop) await api.put(`/loops/${loop.id}`, payload);
      else await api.post(`/clients/${client.id}/loops`, payload);
      toast.success(loop ? "Service loop updated" : "Service loop added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testid="loop-dialog" subtitle={loop ? `For ${loop.client_name || "client"}` : client ? `For ${client.name}` : "Service"} title={loop ? "Edit service loop" : "New service loop"}>
      <div className="space-y-4">
        <div>
          <span className="label-mono text-[var(--text-2)]">Service title</span>
          <input
            data-testid="loop-title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Lawn Mowing"
            className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="label-mono text-[var(--text-2)]">Time</span>
            <input type="time" data-testid="loop-time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          </div>
          <div>
            <span className="label-mono text-[var(--text-2)]">Start date</span>
            <input type="date" data-testid="loop-start" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          </div>
        </div>
        <RecurrenceBuilder value={form.recurrence} onChange={(r) => set("recurrence", r)} />
        <ChannelPicker value={form.channels} onChange={(c) => set("channels", c)} />
        {form.channels.includes("voice") && (
          <VoiceScriptField value={form.voice_script} onChange={(v) => set("voice_script", v)} testid="loop-voice-script" />
        )}
        <div className="flex gap-3 pt-2">
          <button data-testid="loop-cancel" onClick={onClose} className="flex-1 h-11 border border-[var(--line)] hover:bg-white transition-colors">Cancel</button>
          <button data-testid="loop-save" onClick={save} disabled={busy} className="flex-1 h-11 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">{busy ? "Saving…" : loop ? "Save loop" : "Add loop"}</button>
        </div>
      </div>
    </Modal>
  );
}
